import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ItemUserValue } from '@/common/api/types/items';
import { FieldWithStatusIndicator } from '@/features/items/components/item-field-value/FieldWithStatusIndicator';
import { UserFieldDisplay } from './UserFieldDisplay';
import { UserSelectionDialog } from './UserSelectionDialog';

export const UserField = ({
  id,
  name,
  value,
  isEditable = false,
  autoFocus = false,
  onChange,
  isPending,
  isSuccess,
  isError = false,
  error,
}: {
  id: string;
  name: string;
  value?: ItemUserValue;
  isEditable?: boolean;
  autoFocus?: boolean;
  onChange?: (value: string | undefined) => void;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: string;
}) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(autoFocus && !value?.USER);
  const inputId = `user-field-${id}`;

  const handleSave = (userId: string) => {
    if (onChange) {
      onChange(userId);
    }
  };

  const handleClear = () => {
    if (onChange) {
      onChange(undefined);
    }
  };

  return (
    <FieldWithStatusIndicator
      inputId={inputId}
      name={name}
      isPending={isPending}
      isSuccess={isSuccess}
    >
      <div className="flex flex-col gap-1">
        <UserFieldDisplay
          userId={value?.USER}
          isEditable={isEditable}
          onClick={isEditable && !isPending ? () => setDialogOpen(true) : undefined}
          onRemove={isEditable && !isPending ? handleClear : undefined}
        />
        {isError && error && <span className="text-xs text-text-error">{error}</span>}
      </div>

      {isEditable && (
        <UserSelectionDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          selectedUserId={value?.USER}
          onSave={handleSave}
          title={t(($) => $.adaptivePlanner.itemPanel.userField.selectUser)}
        />
      )}
    </FieldWithStatusIndicator>
  );
};
